import React, { useState, useCallback, useRef, Fragment, useContext } from 'react';
import { ExcelRenderer } from 'react-excel-renderer';
import { Modal, Button, Form, Dropdown, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import styles from './ImportContacts.module.css';
import AlertContext from '../../../context/AlertModelContext/AlertModalContext';
import { batchImportContacts } from '../../../settings';

const ImportContacts = ({ show, handleClose, handleImport }) => {
    const alertCtx = useContext(AlertContext);
    const { t } = useTranslation('common');
    const [contacts, setContacts] = useState([]);
    const [fileSelected, setFileSelected] = useState(false);
    const [error, setError] = useState('');
    const [duplicates, setDuplicates] = useState([]);
    const [colFileState, setColFileState] = useState([]);
    const [rowFileState, setRowFileState] = useState([]);
    const [matchingFieldsState, setMatchingFieldsState] = useState({
        fullName: undefined,
        mainPhone: undefined,
        email: undefined,
        remarks: undefined,
        fromCompanyName: undefined
    });
    const [loading, setLoading] = useState(false);
    let fileExplorer = useRef();

    const formatAndValidateIsraeliPhoneNumber = (phone) => {
        let formattedPhone = String(phone).replace(/-/g, '').replace(/^\+972/, '0').replace(/^972/, '0');
        if (!formattedPhone.startsWith('0')) {
            formattedPhone = '0' + formattedPhone;
        }
        const israeliPhonePattern = /^(0)([23489]|5[012345789]|7[2346789])[0-9]{7}$/;
        const isValid = israeliPhonePattern.test(formattedPhone);
        return {
            isValid,
            formattedPhone,
        };
    };

    const resetStates = () => {
        setContacts([]);
        setFileSelected(false);
        setError('');
        setDuplicates([]);
        setColFileState([]);
        setRowFileState([]);
        setMatchingFieldsState({ fullName: undefined, mainPhone: undefined, email: undefined, remarks: undefined, fromCompanyName: undefined });
        setLoading(false);

        if (fileExplorer.current) {
            fileExplorer.current.value = null;
        }
    };

    const fileHandler = useCallback((event) => {
        let fileObj = event.target.files[0];
        if (!fileObj) {
            setError(t('Please select a file'));
            return;
        }

        if (fileObj.type !== 'application/vnd.ms-excel' && fileObj.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            setError(t('Unsupported file type. Please upload an Excel file.'));
            setFileSelected(false);
            return;
        }

        setError('');
        setFileSelected(true);

        ExcelRenderer(fileObj, (err, resp) => {
            if (err) {
                console.log(err);
                setError(t('Error reading the file. Please try again.'));
            } else {
                let rowsCopy = [...resp.rows].filter(rowItem => rowItem.length > 0);
                setColFileState([...resp.rows[0]]);
                setRowFileState(rowsCopy.splice(1, rowsCopy.length));
            }
        });
    }, [t]);

    const chunkArray = (array, chunkSize) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (fileSelected) {
            setLoading(true);

            // Check if required fields are selected
            if (matchingFieldsState.fullName === undefined || matchingFieldsState.mainPhone === undefined) {
                setError(t('Please select columns for Full Name and Phone Number'));
                setLoading(false);
                return;
            }

            let sendMsgToFileError = '';
            const importedContacts = rowFileState.map((row, rowIndex) => {
                const { isValid, formattedPhone } = formatAndValidateIsraeliPhoneNumber(row[matchingFieldsState.mainPhone]);

                if (!isValid) {
                    sendMsgToFileError += `File row ${rowIndex + 1}) Wrong phone number "${row[matchingFieldsState.mainPhone]}" column "${colFileState[matchingFieldsState.mainPhone]}"\n`;
                }

                const contact = {
                    fullName: row[matchingFieldsState.fullName],
                    mainPhone: formattedPhone
                };

                if (matchingFieldsState.email !== undefined && row[matchingFieldsState.email]) {
                    contact.email = row[matchingFieldsState.email];
                }
                if (matchingFieldsState.remarks !== undefined) {
                    contact.remarks = row[matchingFieldsState.remarks] || '';
                }
                if (matchingFieldsState.fromCompanyName !== undefined) {
                    contact.fromCompanyName = row[matchingFieldsState.fromCompanyName] || '';
                }
                return contact;
            });

            if (sendMsgToFileError) {
                setError(sendMsgToFileError);
                setLoading(false);
                return;
            }

            const chunkedContacts = chunkArray(importedContacts, batchImportContacts); // Adjust chunk size as needed

            for (let chunk of chunkedContacts) {
                const result = await handleImport(chunk);
                if (result && result.error) {
                    setError(t(result.error.message));
                    setDuplicates(result.error.duplicates);
                    setLoading(false);
                    return;
                }
            }

            handleClose();
            resetStates();
            alertCtx.showAlert(t('success'), t('Success importing contacts'), 'sm');
        } else {
            console.error('Please upload a valid file');
            setError(t('Please upload a supported file (.xls, .xlsx)'));
        }
    };

    const onColFieldSelected = (field, colField, index) => {
        setMatchingFieldsState({ ...matchingFieldsState, [field]: index });
    };

    const propertyRows = Object.keys(matchingFieldsState).map((key, index) => {
        let title;
        switch (key) {
            case 'fullName':
                title = t('Full Name');
                break;
            case 'mainPhone':
                title = t('Main Phone Number');
                break;
            case 'email':
                title = t('Email');
                break;
            case 'remarks':
                title = t('Remarks');
                break;
            case 'fromCompanyName':
                title = t('Company Name');
                break;
            default:
                title = key;
        }
        return (
            <Fragment key={index + 'Frag'}>
                <div className="propertyRow" key={index}>
                    <p className={styles.colTitle} key={index + '1'}>{title}:</p>
                    <Dropdown key={index + '2'} className={styles.styleDropdown}>
                        <Dropdown.Toggle className={styles.DropdownButton} variant="info" id="dropdown-basic">
                            {matchingFieldsState[key] !== undefined ? colFileState[matchingFieldsState[key]] : t('Select Column To Import')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {colFileState.map((colField, idx) => (
                                <Dropdown.Item key={idx} onClick={() => onColFieldSelected(key, colField, idx)}>
                                    {colField}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </Fragment>
        );
    });

    return (
        <Modal show={show} onHide={() => { handleClose(); resetStates(); }} className={`${styles.modalCustomSize} ${styles.rtlModal}`} size="lg">
            <Modal.Header>
                <Modal.Title>{t('Import Contacts')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBodyStyle}>
                {error && <Alert variant="danger">{error}</Alert>}
                {duplicates.length > 0 && (
                    <div>
                        <h5>{t('Duplicate Contacts')}</h5>
                        <ul>
                            {duplicates.map((contact, index) => (
                                <p key={index}>{`${contact.fullName} (${contact.email}, ${contact.mainPhone})`}</p>
                            ))}
                        </ul>
                    </div>
                )}
                <Form onSubmit={handleSubmit}>
                    <Form.Group>
                        <Form.File
                            className={styles.formFileStyle}
                            label={t('Upload Excel File Import Contacts')}
                            onChange={fileHandler}
                            accept=".xlsx, .xls"
                            required
                        />
                    </Form.Group>
                    {propertyRows.length > 0 && (
                        <div>
                            <h5>{t('Map Columns Import Contacts')}:</h5>
                            {propertyRows}
                        </div>
                    )}
                </Form>

            </Modal.Body>
            <Modal.Footer>
                <Button className='btn-success' type='submit' variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : t('Import Contacts')}
                </Button>
                <Button className="mr-auto" variant="secondary" onClick={() => { handleClose(); resetStates(); }}>
                    {t('Close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImportContacts;
